import dayjs from 'dayjs';

import Divider from '../../../../components/Divider';
import Tooltip from '../../../../components/Tooltip';
import { nexyColors } from '../../../../theme';
import { NexoyaBiddingStrategyType } from '../../../../types';
import { READABLE_FORMAT } from '../../../../utils/dates';
import {
  FormulaTooltipContent,
  FormulaTooltipHeader,
  FormulaTooltipRow,
  FormulaTooltipTableContainer,
  LabelStyled,
  StyledSpan,
} from '../../styles/OptimizationProposal';
import { IRowBiddingStrategy } from './optimizationDetailsTableTypes';
import { renderBiddingStrategyValueCell, translateBiddingStrategyType } from './utils';

const ROAS_BIDDING_STRATEGY_TYPES = [
  NexoyaBiddingStrategyType.TargetRoas,
  NexoyaBiddingStrategyType.MaximizeConversionValue,
];
const CPA_BIDDING_STRATEGY_TYPES = [NexoyaBiddingStrategyType.TargetCpa, NexoyaBiddingStrategyType.MaximizeConversions];

/**
 * The realized ROAS or CPA of the last 28 days before the optimization, matching the bidding strategy's target type.
 */
export const getActualBiddingStrategyValue = (biddingStrategy: IRowBiddingStrategy) => {
  const performance = biddingStrategy?.performance;
  if (!performance) return null;
  if (ROAS_BIDDING_STRATEGY_TYPES.includes(biddingStrategy.type)) {
    return typeof performance.roas === 'number' ? { label: 'Actual ROAS', value: performance.roas } : null;
  }
  if (CPA_BIDDING_STRATEGY_TYPES.includes(biddingStrategy.type)) {
    return typeof performance.cpa === 'number' ? { label: 'Actual CPA', value: performance.cpa } : null;
  }
  return null;
};

export const InitialBiddingStrategyCell = ({
  initialBiddingStrategy,
}: {
  initialBiddingStrategy: IRowBiddingStrategy;
}) => {
  const targetLabel = translateBiddingStrategyType(initialBiddingStrategy?.type);
  const actual = getActualBiddingStrategyValue(initialBiddingStrategy);
  const performance = initialBiddingStrategy?.performance;

  return (
    <Tooltip
      variant="dark"
      content={
        actual ? (
          <FormulaTooltipContent>
            <FormulaTooltipTableContainer>
              <FormulaTooltipHeader>
                Last 28 days ({dayjs(performance.dateFrom).format(READABLE_FORMAT)} -{' '}
                {dayjs(performance.dateTo).format(READABLE_FORMAT)})
              </FormulaTooltipHeader>
              <Divider margin="0" style={{ background: nexyColors.charcoalGrey }} />
              <FormulaTooltipRow>
                <StyledSpan>{targetLabel}</StyledSpan>
                {renderBiddingStrategyValueCell(initialBiddingStrategy)}
              </FormulaTooltipRow>
              <FormulaTooltipRow>
                <StyledSpan>{actual.label}</StyledSpan>
                {renderBiddingStrategyValueCell({ type: initialBiddingStrategy.type, value: actual.value })}
              </FormulaTooltipRow>
            </FormulaTooltipTableContainer>
          </FormulaTooltipContent>
        ) : (
          targetLabel
        )
      }
      popperProps={{
        style: {
          zIndex: 3305,
        },
      }}
    >
      <div className="flex flex-col items-center">
        {renderBiddingStrategyValueCell(initialBiddingStrategy)}
        <LabelStyled>{targetLabel}</LabelStyled>
        {actual ? (
          <div
            className="flex items-center gap-1 whitespace-nowrap text-xs"
            style={{ color: nexyColors.blueyGrey }}
            data-testid="initial-bidding-strategy-actual"
          >
            <span>Actual 28d:</span>
            {renderBiddingStrategyValueCell({ type: initialBiddingStrategy.type, value: actual.value })}
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};
